/**
 * GTM and App Insights tracking utilities
 * Centralizes all tracking logic to eliminate duplication and ensure consistency
 *
 * SINGLE RESPONSIBILITY: This module handles ONLY tracking concerns
 */

import { trackCustomEvent } from 'utilities/app-insights';
import {
  type GTMEventName,
  type AppInsightsEventName,
  type FilterValues,
  GTM_EVENTS,
  APP_INSIGHTS_EVENTS,
} from './transactionTypes';

// ============================================================================
// GTM TRACKING (Window.dataLayer integration)
// ============================================================================

/**
 * Base GTM event structure
 */
interface GTMEvent {
  event: GTMEventName;
  [key: string]: any;
}

/**
 * Safely push event to GTM dataLayer
 * Guards against missing window.dataLayer in test environments
 */
function pushToGTM(event: GTMEvent): void {
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push(event);
  }
}

// ============================================================================
// FILTER TRACKING
// ============================================================================

/**
 * Determines correct GTM filter event based on modal type
 */
function getFilterEventName(modalType: string | undefined, isReset: boolean): GTMEventName {
  if (isReset) {
    if (modalType === 'card') return GTM_EVENTS.CLEAR_ALL_CARD;
    return GTM_EVENTS.CLEAR_ALL_STANDARD;
  }

  if (modalType === 'card') return GTM_EVENTS.FILTER_CARD;
  if (modalType === 'account') return GTM_EVENTS.FILTER_ACCOUNT;
  return GTM_EVENTS.FILTER_STANDARD;
}

/**
 * Track filter submission to GTM
 * Includes filter values for analytics
 */
export function trackFilterSubmit(filterValues: FilterValues, modalType: string | undefined): void {
  const eventName = getFilterEventName(modalType, false);

  // Master sends different payload shapes per modal type — preserve exactly
  if (modalType === 'account') {
    pushToGTM({ event: eventName, selected: true });
  } else {
    pushToGTM({ event: eventName, button_click: true });
  }
}

/**
 * Track filter reset to GTM
 */
export function trackFilterReset(modalType: string | undefined): void {
  const eventName = getFilterEventName(modalType, true);

  pushToGTM({
    event: eventName,
    button_click: true,
  });
}

// ============================================================================
// PAGINATION TRACKING
// ============================================================================

/**
 * Track pagination change to GTM
 */
export function trackPageChange(pageNumber: number): void {
  pushToGTM({
    event: GTM_EVENTS.PAGE_NUMBER,
    button_click: true,
  });
}

// ============================================================================
// DOWNLOAD TRACKING
// ============================================================================

/**
 * Determines correct GTM download event based on modal type
 */
function getDownloadEventName(modalType: string | undefined): GTMEventName {
  return modalType === 'account' ? GTM_EVENTS.DOWNLOAD_ACCOUNT : GTM_EVENTS.DOWNLOAD_STANDARD;
}

/**
 * Track download action to GTM
 */
export function trackDownload(modalType: string | undefined): void {
  const eventName = getDownloadEventName(modalType);

  pushToGTM({
    event: eventName,
    button_click: true,
  });
}

// ============================================================================
// APP INSIGHTS TRACKING
// ============================================================================

/**
 * User context for App Insights tracking
 */
export interface UserContext {
  loggedInUser: any;
  assignedAccountNumber: string;
}

/**
 * Track filter submission to App Insights
 */
export function trackFilterSubmitInsights(context: UserContext): void {
  trackCustomEvent(
    APP_INSIGHTS_EVENTS.SUBMIT_FILTER,
    context.loggedInUser,
    context.assignedAccountNumber,
    'Clicked Filter button to view filtered Transactions List.'
  );
}

/**
 * Track page change to App Insights
 */
export function trackPageChangeInsights(context: UserContext): void {
  trackCustomEvent(
    APP_INSIGHTS_EVENTS.CHANGE_PAGE,
    context.loggedInUser,
    context.assignedAccountNumber,
    'Clicked to change page of transactions data.'
  );
}

/**
 * Track download to App Insights
 */
export function trackDownloadInsights(context: UserContext): void {
  trackCustomEvent(
    APP_INSIGHTS_EVENTS.DOWNLOAD,
    context.loggedInUser,
    context.assignedAccountNumber,
    'Clicked Download to download list of transactions.'
  );
}
